import { Controller } from "@hotwired/stimulus"

// Keeps the member review screen honest about what it is going to do.
//
// Every line in the bucket starts on a default — a line that would go in right
// now is set to go in, everything else waits — and the administrator changes
// them as they work. This walks the lines on every change and rewrites each
// card's state, the three numbers at the top and the running summary.
//
// The count can be exact because the server stamps every card with whether
// that line would actually be written if it were chosen. Ticking "opret" on a
// line that still collides with somebody does not add to the total; it keeps
// waiting, and the screen says so before the button is pressed rather than
// after.
const STATES = {
  create: { label: "Oprettes",          badge: "ad-badge ad-badge--green" },
  wait:   { label: "Venter til senere", badge: "ad-badge ad-badge--amber" },
  skip:   { label: "Springes over",     badge: "ad-badge" }
}

export default class extends Controller {
  static targets = ["summary", "submit", "created", "waiting", "skipped"]
  static values = { straight: Number }

  connect() {
    this.refresh()
  }

  refresh() {
    const counts = { create: 0, wait: 0, skip: 0 }

    this.element.querySelectorAll("[data-import-row]").forEach((row) => {
      const state = this.stateOf(row)
      counts[state] += 1
      row.dataset.state = state

      const pill = row.querySelector("[data-import-state]")
      if (pill) {
        pill.textContent = STATES[state].label
        pill.className = STATES[state].badge
      }
    })

    // The lines nobody had to be asked about go in whatever happens here.
    const created = this.straightValue + counts.create

    if (this.hasCreatedTarget) this.createdTarget.textContent = created
    if (this.hasWaitingTarget) this.waitingTarget.textContent = counts.wait
    if (this.hasSkippedTarget) this.skippedTarget.textContent = counts.skip
    if (this.hasSummaryTarget) this.summaryTarget.textContent = this.summaryFor(counts, created)
    if (this.hasSubmitTarget) this.updateSubmit(created, counts)
  }

  // Somebody has typed in this line, so the server's verdict on it is out of
  // date and we take their side: a line being corrected counts as one on its
  // way in. The server has the last word when the button is pressed.
  touch(event) {
    const row = event.target.closest("[data-import-row]")
    if (!row) return

    row.dataset.importEdited = "true"
    this.refresh()
  }

  // One answer for every line in a group. Thirty-eight lines that are all
  // already members do not need thirty-eight clicks to be skipped, and the
  // group is the unit that makes the same answer right for all of them.
  decideAll(event) {
    const scope = event.target.closest("[data-import-group]") || this.element

    scope.querySelectorAll(`[data-import-row] input[type=radio][value="${event.params.choice}"]`)
         .forEach((radio) => { radio.checked = true })
    this.refresh()
  }

  // Every line's fields at once, shown or hidden. Each card's disclosure
  // reads its own state off the attribute, so this only has to write it.
  fold(event) {
    this.element.querySelectorAll("[data-controller~=disclosure]")
        .forEach((card) => { card.dataset.disclosureOpenValue = String(event.params.open) })
  }

  // A line set to "opret" that would not hold up is still waiting, whatever
  // the radio says. Anything that is not a create and not a skip waits.
  stateOf(row) {
    const checked = row.querySelector('input[type=radio][name$="[action]"]:checked')
    const action = checked ? checked.value : "wait"

    if (action === "skip") return "skip"
    if (action !== "create") return "wait"

    const holds = row.dataset.importCreatable === "true" || row.dataset.importEdited === "true"

    return holds ? "create" : "wait"
  }

  summaryFor({ wait, skip }, created) {
    const parts = [ `${this.lines(created)} bliver oprettet` ]
    if (wait > 0) parts.push(`${this.lines(wait)} bliver liggende`)
    if (skip > 0) parts.push(`${this.lines(skip)} bliver kasseret`)
    return parts.join(", ") + "."
  }

  // The button saves the corrections as well as importing, so it stays live
  // as long as there is a line on the screen — a pass that writes nobody but
  // records an afternoon's typing is worth pressing.
  //
  // The dialog names what is thrown away as well as what is written. Skipping
  // is the one thing on this screen that cannot be undone, and a bulk button
  // makes it easy to do to a lot of lines at once.
  updateSubmit(created, { create, wait, skip }) {
    this.submitTarget.disabled = created === 0 && create + wait + skip === 0

    const question =
      created > 0 ? `Importér ${this.members(created)} i Cirkler?` :
      skip > 0    ? `Kassér ${this.lines(skip)} uden at oprette nogen?` :
                    "Gem rettelserne uden at oprette nogen?"
    const warning =
      skip > 0 && created > 0 ? ` ${this.lines(skip)} bliver kasseret. Det kan ikke fortrydes.` :
      skip > 0                ? " Det kan ikke fortrydes." :
                                ""

    this.submitTarget.dataset.turboConfirm = question + warning
  }

  lines(n)   { return n === 1 ? "1 linje" : `${n} linjer` }
  members(n) { return n === 1 ? "1 nyt medlem" : `${n} nye medlemmer` }
}
